import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { offline } from '../lib/offline'
import { uuid } from '../lib/format'
import { compressImage } from '../lib/image'
import { isOnline } from '../lib/env'
import type { RecordType, DiaperColor, DiaperTexture } from '../lib/types'
import { DIAPER_COLORS, DIAPER_TEXTURES } from '../lib/types'
import { useToast } from './Toast'

interface Props {
  onSuccess: () => void
  photosEnabled: boolean
}

const TYPES: { type: RecordType; label: string; emoji: string; className: string }[] = [
  { type: 'pee', label: '嘘嘘', emoji: '💧', className: 'btn-pee' },
  { type: 'poop', label: '臭臭', emoji: '💩', className: 'btn-poop' },
  { type: 'mixed', label: '嘘+臭', emoji: '✨', className: 'btn-mixed' },
]

interface PendingPhoto {
  blob: Blob
  url: string
}

interface DraftRecord {
  type: RecordType
  color: DiaperColor | null
  texture: DiaperTexture | null
  occurred_at: number
  photo: PendingPhoto | null
}

export default function Checkin({ onSuccess, photosEnabled }: Props) {
  const toast = useToast()
  const [draft, setDraft] = useState<DraftRecord | null>(null)
  const [saving, setSaving] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [online, setOnline] = useState(isOnline())
  const [queueCount, setQueueCount] = useState(offline.count())
  const [showCustomTime, setShowCustomTime] = useState(false)

  // draft 打开时给 body 加 class（让 nav 整体下移隐藏）
  useEffect(() => {
    if (draft) {
      document.body.classList.add('has-checkin-draft')
      return () => document.body.classList.remove('has-checkin-draft')
    }
  }, [draft])

  useEffect(() => {
    const onUp = () => setOnline(true)
    const onDown = () => setOnline(false)
    window.addEventListener('online', onUp)
    window.addEventListener('offline', onDown)
    return () => {
      window.removeEventListener('online', onUp)
      window.removeEventListener('offline', onDown)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (draft?.photo) URL.revokeObjectURL(draft.photo.url)
    }
  }, [draft])

  function selectType(type: RecordType) {
    if (draft && draft.type === type) {
      // 再次点击同一类型：折叠
      setDraft(null)
    } else {
      setDraft({
        type,
        color: null,
        texture: null,
        occurred_at: Date.now(),
        photo: null,
      })
    }
  }

  function closeDraft() {
    if (draft?.photo) URL.revokeObjectURL(draft.photo.url)
    setDraft(null)
  }

  async function handlePhotoChange(file: File) {
    if (!draft) return
    try {
      setUploadingPhoto(true)
      const blob = await compressImage(file, { maxWidth: 1280, quality: 0.85 })
      const url = URL.createObjectURL(blob)
      setDraft((d) => (d ? { ...d, photo: { blob, url } } : d))
    } catch (err) {
      toast.show(err instanceof Error ? err.message : '照片处理失败')
    } finally {
      setUploadingPhoto(false)
    }
  }

  async function saveDraft() {
    if (!draft || saving) return
    setSaving(true)

    let photo_id: string | undefined
    if (draft.photo) {
      try {
        const r = await api.uploadPhoto(draft.photo.blob)
        photo_id = r.photo.id
        if (r.storage_warning === 'near_limit') {
          toast.show('⚠️ 照片存储接近上限')
        }
      } catch (e) {
        toast.show(`照片上传失败：${e instanceof Error ? e.message : '未知错误'}`)
        setSaving(false)
        return
      }
    }

    const client_uuid = uuid()
    try {
      if (isOnline()) {
        await api.createRecord({
          client_uuid,
          type: draft.type,
          occurred_at: draft.occurred_at,
          color: draft.color || undefined,
          texture: draft.texture || undefined,
          photo_id,
        })
        toast.show('已记录 ✓')
      } else {
        offline.enqueue({
          client_uuid,
          type: draft.type,
          occurred_at: draft.occurred_at,
          note: photo_id ? `photo:${photo_id}` : undefined,
        })
        toast.show('已暂存离线')
      }
      closeDraft()
      setQueueCount(offline.count())
      onSuccess()
    } catch {
      offline.enqueue({
        client_uuid,
        type: draft.type,
        occurred_at: draft.occurred_at,
        note: photo_id ? `photo:${photo_id}` : undefined,
      })
      closeDraft()
      setQueueCount(offline.count())
      onSuccess()
      toast.show('已暂存，联网后同步')
    } finally {
      setSaving(false)
    }
  }

  const showDetails = draft && (draft.type === 'poop' || draft.type === 'mixed')
  const typeMeta = draft ? TYPES.find((t) => t.type === draft.type) : null

  return (
    <section className="checkin-inline">
      <div className="checkin-types">
        {TYPES.map((t) => (
          <button
            key={t.type}
            className={`checkin-btn ${t.className} ${draft?.type === t.type ? 'active' : ''}`}
            onClick={() => selectType(t.type)}
          >
            <span className="emoji">{t.emoji}</span>
            <span className="label">{t.label}</span>
          </button>
        ))}
      </div>

      {draft && typeMeta && (
        <div className="checkin-expand">
          <div className="checkin-expand-head">
            <div className="checkin-current">
              <span className="checkin-current-emoji">{typeMeta.emoji}</span>
              <span className="checkin-current-label">{typeMeta.label}</span>
            </div>
            <button className="checkin-close" onClick={closeDraft} aria-label="取消">×</button>
          </div>

          {showDetails && (
            <>
              <div className="checkin-section">
                <div className="checkin-section-label">臭臭颜色</div>
                <div className="color-picker">
                  {DIAPER_COLORS.map((c) => (
                    <button
                      key={c.value}
                      className={`color-swatch ${draft.color === c.value ? 'selected' : ''}`}
                      onClick={() => setDraft({ ...draft, color: c.value })}
                      aria-label={c.label}
                    >
                      <span className="color-dot" style={{ background: c.cssColor }} />
                      <span className="color-label">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="checkin-section">
                <div className="checkin-section-label">臭臭形状</div>
                <div className="texture-picker">
                  {DIAPER_TEXTURES.map((t) => (
                    <button
                      key={t.value}
                      className={`texture-swatch ${draft.texture === t.value ? 'selected' : ''}`}
                      onClick={() => setDraft({ ...draft, texture: t.value })}
                    >
                      <span className="texture-emoji">{t.emoji}</span>
                      <span className="texture-label">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="checkin-section">
            <div className="checkin-section-label">更换时间</div>
            <div className="time-quick">
              <button
                type="button"
                className={`time-quick-btn ${isSameMinute(draft.occurred_at, Date.now()) ? 'active' : ''}`}
                onClick={() => {
                  setDraft({ ...draft, occurred_at: Date.now() })
                  setShowCustomTime(false)
                }}
              >
                🕐 现在
              </button>
              <button
                type="button"
                className={`time-quick-btn ${isSameMinute(draft.occurred_at, Date.now() - 15 * 60_000) ? 'active' : ''}`}
                onClick={() => {
                  setDraft({ ...draft, occurred_at: Date.now() - 15 * 60_000 })
                  setShowCustomTime(false)
                }}
              >
                −15 分钟
              </button>
              <button
                type="button"
                className={`time-quick-btn ${isSameMinute(draft.occurred_at, Date.now() - 30 * 60_000) ? 'active' : ''}`}
                onClick={() => {
                  setDraft({ ...draft, occurred_at: Date.now() - 30 * 60_000 })
                  setShowCustomTime(false)
                }}
              >
                −30 分钟
              </button>
              <button
                type="button"
                className={`time-quick-btn ${isSameMinute(draft.occurred_at, Date.now() - 60 * 60_000) ? 'active' : ''}`}
                onClick={() => {
                  setDraft({ ...draft, occurred_at: Date.now() - 60 * 60_000 })
                  setShowCustomTime(false)
                }}
              >
                −1 小时
              </button>
              <button
                type="button"
                className={`time-quick-btn ${showCustomTime ? 'active' : ''}`}
                onClick={() => setShowCustomTime((v) => !v)}
              >
                更早…
              </button>
            </div>
            <div className="time-current">
              {formatDraftTime(draft.occurred_at)}
            </div>
            {showCustomTime && (
              <input
                type="datetime-local"
                className="checkin-time"
                value={toLocalISOString(draft.occurred_at)}
                onChange={(e) => {
                  const ts = new Date(e.target.value).getTime()
                  if (!isNaN(ts)) setDraft({ ...draft, occurred_at: ts })
                }}
              />
            )}
          </div>

          {photosEnabled && (
            <div className="checkin-section">
              <div className="checkin-section-label">照片（可选）</div>
              {draft.photo ? (
                <div className="draft-photo">
                  <img src={draft.photo.url} alt="" />
                  <button
                    className="draft-photo-remove"
                    onClick={() => {
                      URL.revokeObjectURL(draft.photo!.url)
                      setDraft({ ...draft, photo: null })
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="photo-pick">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) handlePhotoChange(f)
                      e.target.value = ''
                    }}
                  />
                  <span className="photo-pick-icon">📷</span>
                  <span>{uploadingPhoto ? '处理中...' : '拍照或选图'}</span>
                </label>
              )}
            </div>
          )}
        </div>
      )}

      {draft && (
        <div className="checkin-save-bar">
          <button
            className="btn-save-primary"
            onClick={saveDraft}
            disabled={saving}
          >
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      )}

      {!draft && (
        <p className="checkin-hint">
          点一下选类型
          {queueCount > 0 && <span className="queue-badge"> · 离线 {queueCount} 条</span>}
          {!online && <span className="offline-hint"> · 当前离线</span>}
        </p>
      )}
    </section>
  )
}

function toLocalISOString(ts: number): string {
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function isSameMinute(a: number, b: number): boolean {
  const A = new Date(a)
  const B = new Date(b)
  return (
    A.getFullYear() === B.getFullYear() &&
    A.getMonth() === B.getMonth() &&
    A.getDate() === B.getDate() &&
    A.getHours() === B.getHours() &&
    A.getMinutes() === B.getMinutes()
  )
}

function formatDraftTime(ts: number): string {
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`
  if (sameDay) return `今天 ${hm}`
  return `${d.getMonth() + 1}/${d.getDate()} ${hm}`
}
